import { useActiveWorkspaces } from '@shared/layouts/app-layout/components/sidebar/hooks/use-activeWorkspace';
import { useWorkSpaceSwitch } from '@shared/layouts/app-layout/components/sidebar/hooks/use-workspace-switch';
import { SearchWorkspace } from '@shared/layouts/app-layout/components/sidebar/components/search-workspace';
import { WorkspaceItem } from '@shared/layouts/app-layout/components/sidebar/components/workspace-item';
import { capitalize } from '@shared/utils/capitalize';
import { Button, Empty, Menu, Popover, Skeleton } from 'antd';
import type { MenuProps } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RiArrowLeftRightLine, RiArrowRightSLine } from 'react-icons/ri';

export function SwitchWorkspaceMenuItem() {
  const { t } = useTranslation();
  const {
    data,
    setFilter,
    loadNextRef,
    filter,
    loading: activeWorkspaceLoading,
    hasNextPage,
    initialLoading,
    handleClearFilter,
  } = useActiveWorkspaces();
  const { handleWorkspaceSwitch, loading } = useWorkSpaceSwitch();

  const activeWorkspaces = data?.data;
  const loadMoreSkeleton =
    initialLoading || hasNextPage ? (
      <div ref={loadNextRef}>
        <Skeleton active paragraph={false} />
      </div>
    ) : undefined;

  const items: MenuProps['items'] = useMemo(() => {
    const workspaceItems: MenuProps['items'] =
      activeWorkspaces?.map((workspace) => {
        const isCurrentWorkspace = workspace?.isCurrent;
        return {
          key: String(workspace?.id),
          onClick: () => {
            if (isCurrentWorkspace) return;
            handleWorkspaceSwitch(workspace?.id);
          },
          label: <WorkspaceItem workspace={workspace || {}} />,
          className: 'px-1.5! h-9! my-0.5!',
        };
      }) || [];

    if (!activeWorkspaces?.length && !activeWorkspaceLoading) {
      return [
        {
          key: 'empty',
          disabled: true,
          label: <Empty description={capitalize(t('no_workspaces_found'))} />,
          style: { height: 'auto' },
        },
      ];
    }

    return workspaceItems;
  }, [activeWorkspaces, activeWorkspaceLoading, handleWorkspaceSwitch, t]);

  if (initialLoading || (activeWorkspaces?.length ?? 0) <= 1) return null;

  return (
    <div className="text-sm! font-medium! px-1! pt-1!">
      <Popover
        className="p-0 mr-10!"
        destroyOnHidden
        rootClassName="logout__popover"
        align={{ offset: [-5, 30] }}
        onOpenChange={(open) => {
          if (!open) handleClearFilter();
        }}
        content={
          <div className="bg-surface rounded-md overflow-hidden w-57.25! max-h-97.5! flex flex-col">
            <SearchWorkspace setFilter={setFilter} filter={filter} />
            <div className="overflow-auto flex-1">
              <Menu
                mode="inline"
                inlineCollapsed={false}
                items={[
                  ...(items || []),
                  {
                    key: 'lazy-loader',
                    style: { display: initialLoading || hasNextPage ? 'block' : 'none' },
                    label: loadMoreSkeleton,
                  },
                ]}
                rootClassName="border-none!"
              />
            </div>
          </div>
        }
        trigger="click"
        placement="left"
        arrow={false}
      >
        <Button
          type="text"
          loading={loading}
          className="w-full justify-start! rounded-md pl-3! h-8! min-w-0!"
          icon={<RiArrowLeftRightLine size={13} />}
        >
          <span className="flex items-center gap-2 w-full min-w-0">
            <span className="truncate flex-1">{capitalize(t('switch_workspace'))}</span>
            <RiArrowRightSLine className="ml-auto shrink-0" />
          </span>
        </Button>
      </Popover>
    </div>
  );
}
